import { FC, Fragment, useCallback, useContext } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLayoutProfileConfig } from "@finbackoffice/site-core";
import { ModalsContextV2, UserAccountContextV2 } from "contexts";
import { useSignupConfigV2 } from "hooks";
import { AssetsDirectory, SignupInputFieldName, TranslationScopes } from "@finbackoffice/enums";
import { ProfileLayoutSectionEnum } from "@finbackoffice/fe-core";
import { useProfileLayoutViewData, ProfileItemDataType } from "hooks";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import Button from "components/base/button/Button";
import { RouteNames } from "utils/constants/routeNames";
import AccountMenuItem from "../item/AccountMenuItem";
import styles from "./account-popover.module.sass";

type AccountPopoverProps = {
    onItemClick: () => void;
};

const AccountPopover: FC<AccountPopoverProps> = ({ onItemClick }) => {
    const pathname = usePathname();
    const router = useRouter();
    const { walletModalRef } = useContext(ModalsContextV2);
    const { logout, username, userId, firstName, lastName, userEmail } =
        useContext(UserAccountContextV2);
    const { signupConfigObj } = useSignupConfigV2([SignupInputFieldName.Username]);
    const { getFilteredLayoutProfileConfigs } = useLayoutProfileConfig(
        undefined,
        undefined,
        "profileTablet",
    );
    const allLayoutProfileConfigs = getFilteredLayoutProfileConfigs();

    const onSignout = useCallback(async () => {
        await logout().then(() => {
            void walletModalRef.current?.close();
            if (pathname.includes(RouteNames.ACCOUNT)) {
                router.push("/");
            }
        });
    }, [logout, pathname, router, walletModalRef]);

    const { layoutConfigMap } = useProfileLayoutViewData();

    const renderItem = (
        section: ProfileLayoutSectionEnum,
        data: ProfileItemDataType,
        isSubMenu?: boolean,
    ): React.ReactNode => {
        const { config: subMenuConfig, data: subMenuData } = data?.subItems || {};

        return (
            <Fragment key={section}>
                <AccountMenuItem
                    data={data}
                    onClick={onItemClick}
                    showExtraIcon={section !== ProfileLayoutSectionEnum.Main}
                    isSubItem={isSubMenu}
                />
                {section === ProfileLayoutSectionEnum.Main &&
                    subMenuConfig?.length &&
                    subMenuData &&
                    subMenuConfig.map((section) =>
                        renderItem(
                            section.name,
                            subMenuData[section.name] as ProfileItemDataType,
                            true,
                        ),
                    )}
            </Fragment>
        );
    };

    return (
        <div className={styles.accountPopover} data-testid="account-popover">
            <div className={styles.userInfo}>
                <span>
                    {firstName} {lastName}
                </span>
                <span className={styles.userId}>
                    {signupConfigObj.username ? username : userEmail} /{" "}
                    <Translate tid="account_id" namespace={TranslationScopes.UserAccount} />:
                    {userId}
                </span>
            </div>
            <div className={styles.menu}>
                {allLayoutProfileConfigs.map((section) =>
                    renderItem(section.name, layoutConfigMap[section.name] as ProfileItemDataType),
                )}
            </div>
            <Button
                type="button"
                onClick={onSignout}
                className={styles.signOutBtn}
                data-testid="account-exit">
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/signout.svg`}
                    className="svg-icon"
                />
                <Translate tid="account_exit" namespace={TranslationScopes.UserAccount} />
            </Button>
        </div>
    );
};

export default AccountPopover;
